import {
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges
} from "@angular/core";

@Component({
	selector: 'custom-date-picker-c',
	templateUrl: './custom-date-picker.component.html'
})
export class CustomDatePickerComponent implements OnInit, OnChanges {
	@Input() dateModel;
	@Output() dateModelChange = new EventEmitter<any>();
	@Input() dateView = 'Day';
	@Input() allowFuture = false;
	@Input() dateMinLimit;
	@Input() dateMaxLimit;

	isOpen = false;
	viewMonth: number;
	viewYear: number;
	calendarDays = [];
	selectedDate: Date;
	hoveredWeekStart: Date = null;

	// Monday-first week layout
	monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
	weekDays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

	constructor(private elRef: ElementRef) {}

	ngOnInit() {
		this.init();
	}

	ngOnChanges(changes: SimpleChanges) {
		this.init();
	}

	private init() {
		const raw = this.dateModel;
		this.selectedDate = raw instanceof Date ? raw : new Date(raw || Date.now());
		if (isNaN(this.selectedDate.getTime())) {
			this.selectedDate = new Date();
		}
		this.viewMonth = this.selectedDate.getMonth();
		this.viewYear = this.selectedDate.getFullYear();
		this.buildCalendar();
	}

	get displayValue() {
		if (!this.selectedDate) {
			return '';
		}
		if (this.dateView === 'Month') {
			return this.monthNames[this.selectedDate.getMonth()] + ' ' + this.selectedDate.getFullYear();
		}
		const d = this.selectedDate;
		const formatted = this.pad(d.getMonth() + 1) + '/' + this.pad(d.getDate()) + '/' + d.getFullYear();
		if (this.dateView === 'Week') {
			return 'Week of ' + formatted;
		}
		return formatted;
	}

	private pad(n) {
		return n < 10 ? '0' + n : '' + n;
	}

	toggleCalendar() {
		this.isOpen = !this.isOpen;
		if (this.isOpen) {
			this.viewMonth = this.selectedDate.getMonth();
			this.viewYear = this.selectedDate.getFullYear();
			this.buildCalendar();
		}
	}

	@HostListener('document:click', ['$event'])
	onDocClick(event) {
		if (this.isOpen && !this.elRef.nativeElement.contains(event.target)) {
			this.isOpen = false;
		}
	}

	private buildCalendar() {
		const days = [];
		const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
		// shift day-of-week to Monday=0, Sunday=6
		let startDow = firstOfMonth.getDay();
		startDow = startDow === 0 ? 6 : startDow - 1;

		const startDate = new Date(firstOfMonth);
		startDate.setDate(startDate.getDate() - startDow);

		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const minDate = this.dateMinLimit ? new Date(this.dateMinLimit) : null;
		const maxDate = this.dateMaxLimit ? new Date(this.dateMaxLimit) : null;

		for (let i = 0; i < 42; i++) {
			const d = new Date(startDate);
			d.setDate(startDate.getDate() + i);
			d.setHours(0, 0, 0, 0);

			let disabled = false;
			if (!this.allowFuture && d > today) {
				disabled = true;
			}
			if (minDate && d < minDate) {
				disabled = true;
			}
			if (maxDate && d > maxDate) {
				disabled = true;
			}

			days.push({
				date: d,
				currentMonth: d.getMonth() === this.viewMonth,
				disabled,
				isToday: d.toDateString() === today.toDateString()
			});
		}
		this.calendarDays = days;
	}

	prevMonth() {
		if (this.viewMonth === 0) {
			this.viewMonth = 11;
			this.viewYear--;
		} else {
			this.viewMonth--;
		}
		this.buildCalendar();
	}

	nextMonth() {
		if (this.viewMonth === 11) {
			this.viewMonth = 0;
			this.viewYear++;
		} else {
			this.viewMonth++;
		}
		this.buildCalendar();
	}

	prevYear() {
		this.viewYear--;
	}

	nextYear() {
		this.viewYear++;
	}

	private getMonday(date: Date): Date {
		const d = new Date(date);
		const dow = d.getDay();
		d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
		return d;
	}

	selectDay(day) {
		if (day.disabled) {
			return;
		}
		this.selectedDate = this.dateView === 'Week' ? this.getMonday(day.date) : new Date(day.date);
		this.isOpen = false;
		this.hoveredWeekStart = null;
		this.dateModelChange.emit(this.selectedDate);
	}

	selectMonth(monthIndex) {
		if (this.isMonthDisabled(monthIndex)) {
			return;
		}
		this.selectedDate = new Date(this.viewYear, monthIndex, 1);
		this.isOpen = false;
		this.dateModelChange.emit(this.selectedDate);
	}

	isSelected(day) {
		return this.selectedDate && day.date.toDateString() === this.selectedDate.toDateString();
	}

	isInActiveWeek(day) {
		const anchor = this.hoveredWeekStart || (this.selectedDate ? this.getMonday(this.selectedDate) : null);
		if (!anchor) {
			return false;
		}
		const end = new Date(anchor);
		end.setDate(anchor.getDate() + 6);
		return day.date >= anchor && day.date <= end;
	}

	onDayHover(day) {
		if (this.dateView === 'Week') {
			this.hoveredWeekStart = this.getMonday(day.date);
		}
	}

	onDayLeave() {
		if (this.dateView === 'Week') {
			this.hoveredWeekStart = null;
		}
	}

	isSelectedMonth(monthIndex) {
		return this.selectedDate &&
			this.selectedDate.getFullYear() === this.viewYear &&
			this.selectedDate.getMonth() === monthIndex;
	}

	isMonthDisabled(monthIndex) {
		if (!this.allowFuture) {
			const today = new Date();
			if (this.viewYear > today.getFullYear()) {
				return true;
			}
			if (this.viewYear === today.getFullYear() && monthIndex > today.getMonth()) {
				return true;
			}
		}
		return false;
	}
}
